import type {
  StorefrontNewsletterCollectionConfig,
  StorefrontContentRichTextDocument,
} from './storefront-engine';

/**
 * Fixed website templates — frozen shared contract (design amendments A1–A16).
 *
 * Structure lives only in the immutable template manifest. Store revisions
 * carry values only (`StoreWebsiteDocument`). Behavior lives in code.
 */

export type WebsiteInterfaceLocale = 'ar' | 'he' | 'en';
export const WEBSITE_INTERFACE_LOCALES = [
  'ar',
  'he',
  'en',
] as const satisfies readonly WebsiteInterfaceLocale[];

/** Epic #228 P3: per-template drafts (the v3 RPCs). */
export const WEBSITE_CLIENT_CONTRACT = 'fixed-websites-template-drafts-v1' as const;

/** Frozen recovery URLs: each supported client probes only its own exact profile. */
export const WEBSITE_RUNTIME_PATH =
  '/website-runtime/fixed-websites-template-drafts-v1.json' as const;

/**
 * Every released client keeps finding its own document after a newer deploy:
 * the Beauty-era bundles probe `/website-runtime.json`, the Luxury-era (P2)
 * bundles their own path. Paths and tokens are frozen once released.
 */
export const WEBSITE_RUNTIME_RECOVERY_PROFILES = [
  {
    path: '/website-runtime.json',
    clientContract: 'fixed-websites-fashion-beauty-bindings-v1',
  },
  {
    path: '/website-runtime/fixed-websites-fashion-beauty-luxury-bindings-v1.json',
    clientContract: 'fixed-websites-fashion-beauty-luxury-bindings-v1',
  },
  {
    path: WEBSITE_RUNTIME_PATH,
    clientContract: WEBSITE_CLIENT_CONTRACT,
  },
] as const;

export type WebsiteLayoutFamily =
  | 'atelier'
  | 'stride'
  | 'legacy-engine-v2'
  | 'editorial-fashion'
  | 'beauty'
  | 'luxury-jewelry';
export const WEBSITE_LAYOUT_FAMILIES = [
  'atelier',
  'stride',
  'legacy-engine-v2',
  'editorial-fashion',
  'beauty',
  'luxury-jewelry',
] as const satisfies readonly WebsiteLayoutFamily[];

export type WebsitePageKind =
  | 'home'
  | 'catalog'
  | 'product'
  | 'cart'
  | 'checkout'
  | 'order'
  | 'store-info';

/** Order = editor page picker order. */
export const WEBSITE_PAGE_KINDS = [
  'home',
  'catalog',
  'product',
  'cart',
  'checkout',
  'order',
  'store-info',
] as const satisfies readonly WebsitePageKind[];

export type WebsiteComponentId =
  | 'chrome.announcement'
  | 'chrome.header'
  | 'chrome.footer'
  | 'chrome.newsletter'
  | 'home.hero'
  | 'home.categoryTiles'
  | 'home.productRail'
  | 'home.productGrid'
  | 'home.promoBanner'
  | 'home.featureStrip'
  | 'home.aboutTeaser'
  | 'home.categoryCampaign'
  | 'home.newsletter'
  | 'home.mission'
  | 'home.community'
  | 'catalog.listing'
  | 'product.detail'
  | 'product.relatedRail'
  | 'cart.page'
  | 'checkout.page'
  | 'order.confirmation'
  | 'storeInfo.page'
  | 'legacy.enginePage';

export type WebsiteSlotType =
  | 'text'
  | 'rich-text'
  | 'link'
  | 'media'
  | 'category-ref'
  | 'product-ref'
  | 'custom-collection-ref'
  | 'newsletter-collection';

export const WEBSITE_SLOT_TYPES = [
  'text',
  'rich-text',
  'link',
  'media',
  'category-ref',
  'product-ref',
  'custom-collection-ref',
  'newsletter-collection',
] as const satisfies readonly WebsiteSlotType[];

/** Closed; drives the Admin label key `admin.websiteTemplates.slot.<semantic>`. */
export type WebsiteSlotSemantic =
  | 'announcement.text'
  | 'brand.tagline'
  | 'hero.heading'
  | 'hero.subheading'
  | 'hero.ctaLabel'
  | 'hero.ctaLink'
  | 'hero.media'
  | 'category.tile'
  | 'category.tileImage'
  | 'section.heading'
  | 'section.body'
  | 'feature.title'
  | 'feature.body'
  | 'promo.heading'
  | 'promo.body'
  | 'promo.media'
  | 'promo.ctaLabel'
  | 'promo.ctaLink'
  | 'about.heading'
  | 'about.body'
  | 'about.media'
  | 'policy.delivery'
  | 'policy.returns'
  | 'contact.note'
  | 'product.careNote'
  | 'product.sizeGuide'
  | 'cart.note'
  | 'checkout.note'
  | 'confirmation.message'
  | 'footer.note'
  | 'mission.body'
  | 'mission.ctaLabel'
  | 'mission.ctaLink'
  | 'community.heading'
  | 'community.media'
  | 'community.ctaLabel'
  | 'community.ctaLink'
  | 'newsletter.heading'
  | 'newsletter.body'
  | 'newsletter.collection'
  | 'legacy.text'
  | 'legacy.media';

export const WEBSITE_SLOT_SEMANTICS = [
  'announcement.text',
  'brand.tagline',
  'hero.heading',
  'hero.subheading',
  'hero.ctaLabel',
  'hero.ctaLink',
  'hero.media',
  'category.tile',
  'category.tileImage',
  'section.heading',
  'section.body',
  'feature.title',
  'feature.body',
  'promo.heading',
  'promo.body',
  'promo.media',
  'promo.ctaLabel',
  'promo.ctaLink',
  'about.heading',
  'about.body',
  'about.media',
  'policy.delivery',
  'policy.returns',
  'contact.note',
  'product.careNote',
  'product.sizeGuide',
  'cart.note',
  'checkout.note',
  'confirmation.message',
  'footer.note',
  'mission.body',
  'mission.ctaLabel',
  'mission.ctaLink',
  'community.heading',
  'community.media',
  'community.ctaLabel',
  'community.ctaLink',
  'newsletter.heading',
  'newsletter.body',
  'newsletter.collection',
  'legacy.text',
  'legacy.media',
] as const satisfies readonly WebsiteSlotSemantic[];

/**
 * Epic #228 P2: slot semantics the sidebar owns (Admin -> Categories owns a
 * category's name, cover and order). Their slots stay in every manifest, but
 * nothing edits, saves, checks or renders them. Mirrored by SQL
 * `website_internal.website_slot_semantic_retired`. Data only, so leaf modules
 * such as `lib/website-templates/edit-regions` can read it from here.
 */
export const WEBSITE_RETIRED_SLOT_SEMANTICS = [
  'category.tileImage',
  'category.tile',
] as const satisfies readonly WebsiteSlotSemantic[];

export type WebsiteMediaKind = 'image' | 'gif' | 'video';
export const WEBSITE_MEDIA_KINDS = [
  'image',
  'gif',
  'video',
] as const satisfies readonly WebsiteMediaKind[];

export type WebsiteEmptyBehavior = 'omit-item' | 'hide-block' | 'platform-placeholder';
export const WEBSITE_EMPTY_BEHAVIORS = [
  'omit-item',
  'hide-block',
  'platform-placeholder',
] as const satisfies readonly WebsiteEmptyBehavior[];

export type WebsiteMediaAspect = '1:1' | '4:5' | '3:4' | '16:9' | '21:9' | '3:1';
export const WEBSITE_MEDIA_ASPECTS = [
  '1:1',
  '4:5',
  '3:4',
  '16:9',
  '21:9',
  '3:1',
] as const satisfies readonly WebsiteMediaAspect[];

/**
 * The kinds a manifest may list in `linkTargets` (epic #228 P5, PD3). It
 * never holds `external`: every link slot admits that kind unless the
 * manifest opts out with `externalLinks: false` (`websiteAllowedLinkKinds`).
 */
export type WebsiteLinkTargetKind =
  | 'category'
  | 'product'
  | 'custom-collection'
  | 'store-page'
  | 'contact';
export const WEBSITE_LINK_TARGET_KINDS = [
  'category',
  'product',
  'custom-collection',
  'store-page',
  'contact',
] as const satisfies readonly WebsiteLinkTargetKind[];
/**
 * Every destination kind a stored link value can have, `none` aside: the
 * manifest kinds plus the platform-level `external` (Instagram/Facebook).
 */
export type WebsiteDestinationKind = WebsiteLinkTargetKind | 'external';
export const WEBSITE_DESTINATION_KINDS = [
  ...WEBSITE_LINK_TARGET_KINDS,
  'external',
] as const satisfies readonly WebsiteDestinationKind[];

export type WebsiteStorePageLink = 'home' | 'catalog' | 'shop' | 'store-info' | 'sales';
export const WEBSITE_STORE_PAGE_LINKS = [
  'home',
  'catalog',
  'shop',
  'store-info',
  'sales',
] as const satisfies readonly WebsiteStorePageLink[];

export type WebsiteContactMethod = 'whatsapp' | 'phone' | 'email';
export const WEBSITE_CONTACT_METHODS = [
  'whatsapp',
  'phone',
  'email',
] as const satisfies readonly WebsiteContactMethod[];

export type WebsiteRichTextDocument = StorefrontContentRichTextDocument;

/** Same closed collection contract as F119; absent/empty slots disable collection. */
export type WebsiteNewsletterCollection = StorefrontNewsletterCollectionConfig;
export const WEBSITE_NEWSLETTER_SOURCE_ID = 'website-newsletter' as const;

export type WebsiteSlotDefault =
  | {
      readonly kind: 'localized-text';
      readonly ar: string;
      readonly he: string;
      readonly en: string;
    }
  /** Platform asset in the `website-template-assets` bucket. */
  | { readonly kind: 'template-media'; readonly assetKey: string }
  | { readonly kind: 'link'; readonly value: WebsiteLinkValue }
  /** Ref slots: the block's template data source picks. */
  | { readonly kind: 'auto' }
  | { readonly kind: 'none' };

export interface WebsiteSlotSchema {
  /** `/^[a-z][a-zA-Z0-9]*(\.[a-zA-Z0-9-]+){1,6}$/`, ≤96 chars, unique per version. */
  readonly id: string;
  readonly type: WebsiteSlotType;
  readonly semantic: WebsiteSlotSemantic;
  /** 1..12 for "Slide 2", "Tile 3" labels. */
  readonly ordinal?: number;
  /** Must resolve to non-empty (default or authored). */
  readonly required: boolean;
  /** Default alone does NOT satisfy readiness. */
  readonly requiresMerchantValue: boolean;
  /** text/rich-text; ≤4000. */
  readonly maxLength?: number;
  readonly multiline?: boolean;
  /** media only. */
  readonly mediaKinds?: readonly WebsiteMediaKind[];
  readonly mediaAspect?: WebsiteMediaAspect;
  /** video only. */
  readonly allowAutoplay?: boolean;
  /** media only: `alt` may be '' for decorative slots. */
  readonly decorative?: boolean;
  readonly linkTargets?: readonly WebsiteLinkTargetKind[];
  /**
   * Epic #228 P5, media only: the image is the anchor of this link slot of
   * the same item. No catalog manifest declares it yet (PD8 option A).
   */
  readonly clickable?: { readonly linkSlotId: string };
  /** Epic #228 P5, link only: `false` opts the slot out of `external`. */
  readonly externalLinks?: boolean;
  readonly defaults: WebsiteSlotDefault;
}

export type WebsiteMerchandisingKey =
  | 'best_sellers'
  | 'trending_now'
  | 'new_arrivals'
  | 'special_offers';
export const WEBSITE_MERCHANDISING_KEYS = [
  'best_sellers',
  'trending_now',
  'new_arrivals',
  'special_offers',
] as const satisfies readonly WebsiteMerchandisingKey[];

export type WebsiteBlockDataSource =
  | {
      readonly kind: 'merchandising';
      readonly key: WebsiteMerchandisingKey;
      readonly limit: number;
    }
  | { readonly kind: 'public-categories'; readonly limit: number }
  | WebsiteCategoryProductsSource
  | { readonly kind: 'bound-categories'; readonly limit: number };

export interface WebsiteCategoryProductsSource {
  readonly kind: 'category-products';
  readonly categoryOrdinal: 1 | 2 | 3;
  readonly limit: number;
}

export interface PublicWebsiteCategoryProductsSource extends WebsiteCategoryProductsSource {
  readonly categoryId: string | null;
}

export interface PublicWebsiteBoundCategoriesSource {
  readonly kind: 'bound-categories';
  readonly bindings: readonly { readonly ordinal: number; readonly categoryId: string | null }[];
  readonly limit: number;
}

export type PublicWebsiteBlockDataSource =
  | Exclude<
      WebsiteBlockDataSource,
      WebsiteCategoryProductsSource | { readonly kind: 'bound-categories'; readonly limit: number }
    >
  | PublicWebsiteCategoryProductsSource
  | PublicWebsiteBoundCategoriesSource;

export interface PublicProductCardMedia {
  readonly productId: string;
  readonly imageUrls: readonly string[];
}

export interface WebsiteBlockManifest {
  /** Unique per page (and per chrome). `/^[a-z][a-zA-Z0-9]*$/`. */
  readonly id: string;
  readonly component: WebsiteComponentId;
  /** Closed per component+family, checked against capabilities. */
  readonly variant: string;
  readonly slotIds: readonly string[];
  readonly emptyBehavior: WebsiteEmptyBehavior;
  readonly dataSource?: WebsiteBlockDataSource;
}

export type WebsiteColorToken = 'brand' | 'onBrand' | 'accent' | 'background' | 'surface' | 'text';
export const WEBSITE_COLOR_TOKENS = [
  'brand',
  'onBrand',
  'accent',
  'background',
  'surface',
  'text',
] as const satisfies readonly WebsiteColorToken[];

/**
 * Heading/body pairs resolve to a stack covering Arabic+Hebrew+Latin. The
 * woff2 files are self-hosted under `apps/storefront/public/fonts`.
 */
export type WebsiteFontId =
  | 'ibm-plex-sans-arabic'
  | 'amiri'
  | 'noto-sans-hebrew'
  | 'frank-ruhl-libre'
  | 'inter'
  | 'fraunces';
export const WEBSITE_FONT_IDS = [
  'ibm-plex-sans-arabic',
  'amiri',
  'noto-sans-hebrew',
  'frank-ruhl-libre',
  'inter',
  'fraunces',
] as const satisfies readonly WebsiteFontId[];

export interface WebsiteThemeSchema {
  /**
   * A17: a platform-authoring constraint for catalog tooling. It grants no
   * merchant authority; the shape stays because manifests are immutable.
   */
  readonly colorTokens: readonly WebsiteColorToken[];
  /** `#rrggbb`. */
  readonly defaults: Readonly<Record<WebsiteColorToken, string>>;
  readonly fontOptions: {
    readonly heading: readonly WebsiteFontId[];
    readonly body: readonly WebsiteFontId[];
  };
  readonly defaultFonts: { readonly heading: WebsiteFontId; readonly body: WebsiteFontId };
  /** Gallery card swatches (≤4). */
  readonly swatches: readonly WebsiteColorToken[];
}

export interface WebsitePageManifest {
  readonly blocks: readonly WebsiteBlockManifest[];
}

export interface WebsiteTemplateManifest {
  readonly schemaVersion: 1;
  readonly rendererMajor: 1;
  readonly layoutFamily: WebsiteLayoutFamily;
  /** announcement?, header, footer. */
  readonly chrome: readonly WebsiteBlockManifest[];
  /** ALL 7 page kinds are required. */
  readonly pages: Readonly<Record<WebsitePageKind, WebsitePageManifest>>;
  /** ≤300. */
  readonly slots: readonly WebsiteSlotSchema[];
  readonly theme: WebsiteThemeSchema;
}

export interface WebsiteMediaValue {
  /** uuid, same store, status ready. */
  readonly mediaAssetId: string;
  /** Must equal the asset's server-recorded kind. */
  readonly kind: WebsiteMediaKind;
  /** Required when kind ∈ {gif, video}; image asset of the same store. */
  readonly posterAssetId: string | null;
  /** ≤200, text-safety rules; '' allowed only for decorative slots. */
  readonly alt: string;
  /**
   * Epic #228 P6: where the picture sits in its frame. Absent = the family
   * CSS default (today's output). Never `{}`: at least one key is present.
   */
  readonly framing?: WebsiteMediaFraming;
}

/**
 * Epic #228 P6 (PD1): one frame of a media value. `x`/`y` are the CSS
 * `object-position` fractions (0 = left/top, 1 = right/bottom). They are
 * physical and never mirrored in RTL. `zoom` is relative to the cover scale,
 * so every in-range frame covers its box. At most 4 decimals each.
 */
export interface WebsiteMediaFrame {
  /** 0..1 */
  readonly x: number;
  /** 0..1 */
  readonly y: number;
  /** 1..4 */
  readonly zoom: number;
}

/**
 * Epic #228 P6 (PD1/PD2): per-device frames. Resolution per device is
 * `framing[device] → framing.shared → none` (the family CSS default).
 */
export interface WebsiteMediaFraming {
  readonly shared?: WebsiteMediaFrame;
  readonly desktop?: WebsiteMediaFrame;
  readonly mobile?: WebsiteMediaFrame;
}

export type WebsiteLinkValue =
  | { readonly kind: 'category'; readonly categoryId: string }
  | { readonly kind: 'product'; readonly productId: string }
  | { readonly kind: 'custom-collection'; readonly collectionRef: string }
  | { readonly kind: 'store-page'; readonly page: WebsiteStorePageLink }
  | { readonly kind: 'contact'; readonly method: WebsiteContactMethod }
  /** Epic #228 P5: stored byte for byte; `isAllowedSocialUrl` (≤256, Instagram/Facebook). */
  | { readonly kind: 'external'; readonly url: string }
  | { readonly kind: 'none' };

/** Epic #228 P5: the one destination union every editor and renderer shares. */
export type StorefrontDestination = WebsiteLinkValue;

/**
 * Epic #228: the device a media frame applies to. `desktop` = the viewport
 * matches `WEBSITE_FRAME_DESKTOP_QUERY` (`(min-width: 1024px)`).
 */
export type WebsiteFrameDevice = 'desktop' | 'mobile';

/**
 * Epic #228 P5 (design §4.1): what the merchant may do with one element (an
 * item path such as `home.hero.slide1`). Derived from the manifest by
 * `deriveWebsiteElementCapabilities`; nothing is stored.
 */
export interface WebsiteElementCapabilities {
  readonly elementId: string;
  readonly text: readonly { readonly slotId: string; readonly kind: 'text' | 'rich-text' }[];
  readonly media: readonly {
    readonly slotId: string;
    /** Epic #228 P6: where the slot renders, from the frame registry (`websiteMediaFrame`). */
    readonly device: WebsiteFrameDevice | 'any';
    /**
     * Epic #228 P6: `per-device` = the editor writes `desktop` and `mobile`
     * (two tabs); `shared` = one frame. `none` is kept for unframeable slots;
     * no live slot uses it (retired slots have no capabilities).
     */
    readonly framing: 'none' | 'shared' | 'per-device';
    /** = `!slot.required`. */
    readonly removable: boolean;
    /** `alt` may be ''. */
    readonly decorative: boolean;
  }[];
  /** `null`: the element is not clickable (no link slot). */
  readonly destination: {
    readonly linkSlotId: string;
    readonly allowedKinds: readonly WebsiteDestinationKind[];
    /** The media is the anchor (`clickable`), not only the CTA label. */
    readonly anchorsMedia: boolean;
  } | null;
}

export interface WebsiteCategoryRefValue {
  readonly categoryId: string;
}
export interface WebsiteProductRefValue {
  readonly productId: string;
}
export interface WebsiteCustomCollectionRefValue {
  readonly collectionRef: string;
}

/** Store value side — the ONLY merchant-writable shape. Closed keys. */
export type WebsiteSlotValue =
  | { readonly v: string }
  | { readonly v: WebsiteRichTextDocument }
  | { readonly v: WebsiteLinkValue }
  | { readonly v: WebsiteMediaValue }
  | { readonly v: WebsiteCategoryRefValue }
  | { readonly v: WebsiteProductRefValue }
  | { readonly v: WebsiteCustomCollectionRefValue }
  | { readonly v: WebsiteNewsletterCollection }
  /** Optional slots only. */
  | { readonly empty: true };

/**
 * @deprecated A17. Historical and legacy revision data only. No merchant API
 * accepts a theme: `save_store_website_theme` raises `theme_locked` and a
 * staged template always starts with `{}`.
 */
export interface WebsiteThemeOverride {
  /** `#rrggbb`, only tokens in `schema.colorTokens`. */
  readonly colors?: Partial<Record<WebsiteColorToken, string>>;
  readonly fonts?: { readonly heading?: WebsiteFontId; readonly body?: WebsiteFontId };
}

export interface StoreWebsiteDocument {
  readonly templateVersionId: string;
  readonly slots: Readonly<Record<string, WebsiteSlotValue>>;
  readonly theme: WebsiteThemeOverride;
  readonly interfaceLocale: WebsiteInterfaceLocale;
}

export interface WebsiteResolvedTheme {
  readonly colors: Readonly<Record<WebsiteColorToken, string>>;
  readonly fonts: { readonly heading: WebsiteFontId; readonly body: WebsiteFontId };
}

/**
 * `src`/`posterSrc` wire form is an opaque reference (`media:<uuid>` for store
 * media, `template:<assetKey>` for platform template assets). The shared public
 * service hydrates them into proxy/bucket URLs; raw storage paths never appear.
 */
export type PublicResolvedSlot =
  | { readonly type: 'newsletter-collection'; readonly collection: WebsiteNewsletterCollection }
  | { readonly type: 'text'; readonly text: string }
  | { readonly type: 'rich-text'; readonly doc: WebsiteRichTextDocument }
  /** Server/shared-resolved safe href. */
  | { readonly type: 'link'; readonly href: string }
  | {
      readonly type: 'media';
      readonly kind: WebsiteMediaKind;
      readonly src: string;
      readonly posterSrc: string | null;
      readonly alt: string;
      /** @deprecated A27: renderers ignore it; every video autoplays muted. SQL keeps emitting it. */
      readonly autoplay: boolean;
      /** Epic #228 P6: present only when the merchant framed the media (exact-key DTO). */
      readonly framing?: WebsiteMediaFraming;
    }
  | {
      readonly type: 'category-ref' | 'product-ref' | 'custom-collection-ref';
      readonly ref: string;
    };

export interface PublicWebsiteBlock {
  readonly id: string;
  readonly component: WebsiteComponentId;
  readonly variant: string;
  /** Already resolved: authored ∪ localized default; empties removed. */
  readonly slots: Readonly<Record<string, PublicResolvedSlot>>;
  readonly emptyBehavior: WebsiteEmptyBehavior;
  readonly dataSource?: PublicWebsiteBlockDataSource;
}

export interface PublicStoreWebsiteSite {
  readonly layoutFamily: WebsiteLayoutFamily;
  readonly rendererMajor: 1;
  readonly interfaceLocale: WebsiteInterfaceLocale;
  readonly direction: 'rtl' | 'ltr';
  readonly theme: WebsiteResolvedTheme;
  readonly chrome: readonly PublicWebsiteBlock[];
}

export interface PublicStoreWebsitePage {
  readonly kind: WebsitePageKind;
  readonly blocks: readonly PublicWebsiteBlock[];
}

/**
 * Public DTO — no slot schemas, limits, other-locale defaults, ids other than
 * opaque media/ref ids, checksums or audit data.
 */
export interface PublicStoreWebsiteDto {
  readonly mode: 'template' | 'legacy';
  /**
   * `legacy` mode only: the store's interface language, so store routes of a
   * store that has not published a fixed template still render in the store
   * language (A4). Template mode carries it in `site.interfaceLocale`.
   */
  readonly interfaceLocale?: WebsiteInterfaceLocale;
  readonly site?: PublicStoreWebsiteSite;
  readonly page?: PublicStoreWebsitePage;
  /**
   * `legacy-engine-v2` family, every page kind (the engine config carries the
   * store's theme): the frozen engine page with slot values applied
   * server-side and made render-safe by the legacy public reader.
   */
  readonly legacyEngine?: {
    readonly engineVersion: 1 | 2;
    readonly config: Readonly<Record<string, unknown>>;
  };
}

export type WebsiteIssueCode =
  | 'invalid_slot'
  | 'unknown_slot'
  | 'structure_override'
  | 'unsafe_text'
  | 'text_too_long'
  | 'invalid_media'
  | 'media_kind_not_allowed'
  | 'poster_required'
  | 'cross_store_reference'
  | 'invalid_reference'
  | 'contrast_insufficient'
  | 'unsupported_font'
  | 'unsupported_color_token'
  | 'missing_required'
  | 'missing_merchant_value'
  | 'missing_store_logo'
  | 'stale_draft'
  | 'builder_retired'
  | 'template_unavailable'
  | 'document_too_large'
  | 'rate_limited'
  /** A17: colors and fonts belong to the template. No merchant API changes them. */
  | 'theme_locked'
  /**
   * Epic #228 P2: the slot's semantic is retired (category tiles belong to
   * the category itself). Save RPCs reject any write to it.
   */
  | 'slot_retired';

export const WEBSITE_ISSUE_CODES = [
  'invalid_slot',
  'unknown_slot',
  'structure_override',
  'unsafe_text',
  'text_too_long',
  'invalid_media',
  'media_kind_not_allowed',
  'poster_required',
  'cross_store_reference',
  'invalid_reference',
  'contrast_insufficient',
  'unsupported_font',
  'unsupported_color_token',
  'missing_required',
  'missing_merchant_value',
  'missing_store_logo',
  'stale_draft',
  'builder_retired',
  'template_unavailable',
  'document_too_large',
  'rate_limited',
  'theme_locked',
  'slot_retired',
] as const satisfies readonly WebsiteIssueCode[];

/**
 * Epic #228 P1: `blocking` stops a publish (a value the renderer cannot
 * draw); `warning` is shown but publishes (dead link or reference, unwritten
 * merchant text, no store logo).
 */
export type WebsiteReadinessSeverity = 'blocking' | 'warning';
export const WEBSITE_READINESS_SEVERITIES = [
  'blocking',
  'warning',
] as const satisfies readonly WebsiteReadinessSeverity[];

export interface WebsiteReadinessIssue {
  readonly code: WebsiteIssueCode;
  readonly slotId?: string;
  readonly pageKind?: WebsitePageKind;
  readonly severity: WebsiteReadinessSeverity;
}

export interface WebsiteReadinessReport {
  readonly blocking: readonly WebsiteReadinessIssue[];
  readonly warnings: readonly WebsiteReadinessIssue[];
}

export type WebsiteTemplateCategory =
  | 'jewelry'
  | 'fashion'
  | 'beauty'
  | 'electronics'
  | 'home-living'
  | 'food'
  | 'other';
export const WEBSITE_TEMPLATE_CATEGORIES = [
  'jewelry',
  'fashion',
  'beauty',
  'electronics',
  'home-living',
  'food',
  'other',
] as const satisfies readonly WebsiteTemplateCategory[];

/**
 * A26. `catalog`: a platform template every store may use. `custom`: released
 * by the platform for ONE store and listed for that store only.
 * `legacy-frozen`: the frozen copy of a store's own builder design.
 */
export type WebsiteTemplateKind = 'catalog' | 'custom' | 'legacy-frozen';
export const WEBSITE_TEMPLATE_KINDS = [
  'catalog',
  'custom',
  'legacy-frozen',
] as const satisfies readonly WebsiteTemplateKind[];

export interface WebsiteTemplateCatalogItem {
  readonly templateId: string;
  readonly slug: string;
  readonly currentVersionId: string;
  /** A26/A34: `legacy-frozen` is listed for its owner only, on the first unfiltered page. */
  readonly kind: WebsiteTemplateKind;
  readonly category: WebsiteTemplateCategory;
  readonly styleTags: readonly string[];
  /**
   * Admin i18n keys `admin.websiteTemplates.catalog.<slug>.title|description`.
   * `legacy-frozen`: `admin.websiteTemplates.catalog.legacy.title|description`.
   */
  readonly titleKey: string;
  readonly descriptionKey: string;
  /** Null for `legacy-frozen`: it has no thumbnail, Admin shows a neutral tile. */
  readonly thumbnailSrc: string | null;
  /** `#rrggbb` values (≤4). */
  readonly swatches: readonly string[];
  readonly isCurrent: boolean;
  readonly isInDraft: boolean;
}

export interface WebsiteTemplateCatalogPage {
  readonly items: readonly WebsiteTemplateCatalogItem[];
  readonly nextCursor: string | null;
}

export interface WebsiteNotCarriedSlot {
  readonly slotId: string;
  readonly semantic: WebsiteSlotSemantic;
  readonly ordinal?: number;
}

export interface WebsiteStageResult {
  readonly draftRevisionId: string;
  readonly carriedOver: readonly string[];
  readonly notCarriedOver: readonly WebsiteNotCarriedSlot[];
}

/** A23/A28: the draft's template, for the Admin toolbar (thumbnail, name, status). */
export interface StoreWebsiteEditorTemplate {
  readonly templateId: string;
  readonly slug: string;
  readonly kind: WebsiteTemplateKind;
  readonly category: WebsiteTemplateCategory;
  /** `legacy-frozen`: `admin.websiteTemplates.catalog.legacy.title`. */
  readonly titleKey: string;
  /** Null for `legacy-frozen`. */
  readonly thumbnailAssetKey: string | null;
}

export interface StoreWebsiteEditorState {
  readonly mode: 'template' | 'legacy';
  readonly conversion: 'none' | 'converted' | 'unmappable';
  readonly live: {
    readonly revisionId: string;
    readonly templateVersionId: string;
    readonly publishedAt: string;
  } | null;
  readonly draft: {
    readonly revisionId: string;
    readonly document: StoreWebsiteDocument;
    readonly updatedAt: string;
  } | null;
  /** Pinned draft version (admin-only). */
  readonly manifest: WebsiteTemplateManifest | null;
  readonly templateTitleKey: string | null;
  readonly readiness: readonly WebsiteReadinessIssue[];
  readonly hasUnpublishedChanges: boolean;
  /** The draft's template. Null exactly when `draft` is null. */
  readonly template: StoreWebsiteEditorTemplate | null;
  /** Template of the live revision, or null when nothing is published. */
  readonly liveTemplateId: string | null;
}

/**
 * Epic #228 P3: whether customers see this template now. `live` exactly when
 * the live revision belongs to this template.
 */
export type TemplatePublicStatus = 'live' | 'not-live';
export const TEMPLATE_PUBLIC_STATUSES = [
  'live',
  'not-live',
] as const satisfies readonly TemplatePublicStatus[];

/**
 * Epic #228 P3: one template's saved draft (its head) in a store. Each
 * template keeps its own head, so switching templates never loses edits.
 */
export interface StoreWebsiteTemplateHead {
  readonly templateId: string;
  readonly headRevisionId: string;
  /** The version the head is pinned to. */
  readonly templateVersionId: string;
  /** The template's current platform version. */
  readonly currentVersionId: string;
  /** Server-computed; never computed in TS. Null only while the server has not set it. */
  readonly contentHash: string | null;
  readonly updatedAt: string;
  readonly publicStatus: TemplatePublicStatus;
  /** Server value: live template only, head content differs from live content. */
  readonly hasUnpublishedChanges: boolean;
}

/**
 * Epic #228 P3: the editor state of ONE template, named by the route. Same
 * keys as `StoreWebsiteEditorState` except `live` (adds the template and the
 * content hash) and `readiness` (split by severity), plus the head fields.
 * `draft` describes the head revision, which may be the published one.
 */
export interface StoreWebsiteEditorStateV3 extends Omit<
  StoreWebsiteEditorState,
  'live' | 'readiness' | 'hasUnpublishedChanges'
> {
  /**
   * The template this state describes. Null only for the entry-route read
   * (`templateId` argument null) of a store that has no template to open.
   */
  readonly templateId: string | null;
  /** Null when this template has no saved draft in the store. */
  readonly head: { readonly revisionId: string; readonly contentHash: string | null } | null;
  readonly live: {
    readonly revisionId: string;
    readonly templateId: string;
    readonly templateVersionId: string;
    readonly publishedAt: string;
    readonly contentHash: string | null;
  } | null;
  readonly readiness: WebsiteReadinessReport;
  /** Null exactly when `head` is null. */
  readonly publicStatus: TemplatePublicStatus | null;
  readonly hasUnpublishedChanges: boolean;
  /** The head is pinned to an older version than the one the store may move to. */
  readonly updateAvailable: boolean;
  /** Every head of the store, this template's included. */
  readonly templates: readonly StoreWebsiteTemplateHead[];
}

export interface WebsiteTemplateCatalogItemV3 extends WebsiteTemplateCatalogItem {
  readonly isLive: boolean;
  /** The template old Admin builds open (compatibility only; never used to pick what to edit). */
  readonly isSelected: boolean;
  readonly draft: {
    readonly headRevisionId: string;
    readonly updatedAt: string;
    readonly hasUnpublishedChanges: boolean;
  } | null;
}

export interface WebsiteTemplateCatalogPageV3 {
  readonly items: readonly WebsiteTemplateCatalogItemV3[];
  readonly nextCursor: string | null;
}

/** Result of `save_store_website_slots_v3` and `set_store_website_interface_locale_v3`. */
export interface SaveSlotsV3Result {
  /** The template's new head. */
  readonly draftRevisionId: string;
  readonly contentHash: string | null;
}

export interface PublishV3Result {
  readonly liveRevisionId: string;
  readonly contentHash: string | null;
}

/** `update_store_website_template_version_v3`: the new editor state plus what moved. */
export interface UpdateTemplateVersionV3Result extends StoreWebsiteEditorStateV3 {
  readonly carriedOver: readonly string[];
  readonly notCarriedOver: readonly WebsiteNotCarriedSlot[];
}

export type WebsitePublishActionLabel = 'publish' | 'publishChanges' | 'allPublished';
export const WEBSITE_PUBLISH_ACTION_LABELS = [
  'publish',
  'publishChanges',
  'allPublished',
] as const satisfies readonly WebsitePublishActionLabel[];

export interface WebsitePublishAction {
  readonly label: WebsitePublishActionLabel;
  readonly disabled: boolean;
}

export interface WebsiteDraftMutationResult {
  readonly draftRevisionId: string;
}

export interface WebsitePublishResult {
  readonly liveRevisionId: string;
}

export type StoreWebsiteRevisionSource =
  | 'stage'
  | 'slot'
  | 'theme'
  | 'locale'
  | 'restore'
  | 'legacy-conversion';

export interface StoreWebsiteRevisionSummary {
  readonly revisionId: string;
  readonly revisionNumber: number;
  readonly status: 'draft' | 'published' | 'archived';
  readonly source: StoreWebsiteRevisionSource;
  readonly templateTitleKey: string | null;
  readonly createdAt: string;
  readonly publishedAt: string | null;
}

export interface StoreWebsiteRevisionPage {
  readonly items: readonly StoreWebsiteRevisionSummary[];
  readonly nextCursor: string | null;
}

export interface WebsiteSlotChrome {
  readonly slotId: string;
  /** Admin key `admin.websiteTemplates.slot.<semantic>`; localized by Admin. */
  readonly labelKey: string;
  readonly ordinal?: number;
}

export interface WebsiteCanvasRenderData {
  readonly dto: PublicStoreWebsiteDto;
  readonly slotChrome: readonly WebsiteSlotChrome[];
}

export interface StoreWebsiteConversionReport {
  readonly result: 'converted' | 'unmappable';
  readonly sourceLocale: WebsiteInterfaceLocale;
  readonly mappedSlots: number;
  readonly frozenItems: number;
  /** Closed semantic keys of frozen (locked) items; never raw content. */
  readonly frozenItemKeys: readonly string[];
  readonly discardedLocaleVariants: number;
}

export interface CustomerOrderStoreContext {
  readonly storeSlug: string;
}

export interface WebsiteMediaUploadResult {
  readonly mediaAssetId: string;
  readonly kind: WebsiteMediaKind;
  readonly width: number;
  readonly height: number;
  readonly durationMs?: number;
}
